/**
 * PackagesManagement - Admin-sida för att hantera prispaket
 */

import { useState, useEffect } from 'react';
import { ROUTES } from '@/constants/routes';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useTranslation } from '@/store/languageStore';
import {
  ArrowLeftIcon,
  CubeIcon,
  PlusIcon,
  PencilIcon,
  CurrencyDollarIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import * as HeroIcons from '@heroicons/react/24/outline';
import api from '@/services/api/client';

interface PackageModule {
  id: string;
  name: string;
  displayName: string;
  icon?: string;
  isIncluded: boolean;
}

interface SystemPackage {
  id: string;
  systemId: string;
  name: string;
  displayName: string;
  description?: string;
  priceMonthly: number;
  priceYearly: number;
  isDefault: boolean;
  isActive: boolean;
  maxUsers?: number;
  maxVehicles?: number;
  features?: string[];
  modules: PackageModule[];
}

interface SystemWithPackages {
  id: string;
  name: string;
  displayName: string;
  icon?: string;
  packages: SystemPackage[];
}

interface Module {
  id: string;
  name: string;
  displayName: string;
  icon?: string;
  systemId: string;
}

const getIcon = (iconName: string | undefined, className: string = 'w-5 h-5'): JSX.Element => {
  if (!iconName) return <CubeIcon className={className} />;
  const IconComponent = (HeroIcons as any)[iconName];
  return IconComponent ? <IconComponent className={className} /> : <CubeIcon className={className} />;
};

const formatPrice = (price: number): string => {
  return new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(price);
};

export default function PackagesManagement() {
  const { t } = useTranslation();
  const [systems, setSystems] = useState<SystemWithPackages[]>([]);
  const [allModules, setAllModules] = useState<Module[]>([]);
  const navigate = useNavigate();
  const [expandedSystems, setExpandedSystems] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPackage, setEditingPackage] = useState<SystemPackage | null>(null);
  const [selectedSystemId, setSelectedSystemId] = useState<string>('');
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    displayName: '',
    description: '',
    priceMonthly: 0,
    priceYearly: 0,
    isDefault: false,
    maxUsers: '',
    maxVehicles: '',
    features: '',
    moduleIds: [] as string[],
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [systemsRes, modulesRes] = await Promise.all([
        api.get<{ success: boolean; data: SystemWithPackages[] }>('/api/packages/systems'),
        api.get<{ success: boolean; data: { systems: { modules: Module[] }[] } }>('/api/systems/with-modules'),
      ]);
      
      if (systemsRes.data.success) {
        setSystems(systemsRes.data.data);
        // Expandera alla system som default
        setExpandedSystems(new Set(systemsRes.data.data.map(s => s.id)));
      }
      
      if (modulesRes.data.success) {
        const modules: Module[] = [];
        modulesRes.data.data.systems?.forEach((s: any) => {
          s.modules?.forEach((m: any) => {
            modules.push({ ...m, systemId: s.id });
          });
        });
        setAllModules(modules);
      }
    } catch {
      toast.error(t('packages.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (systemId: string) => {
    setExpandedSystems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(systemId)) {
        newSet.delete(systemId);
      } else {
        newSet.add(systemId);
      }
      return newSet;
    });
  };

  const openCreateModal = (systemId: string) => {
    setSelectedSystemId(systemId);
    setEditingPackage(null);
    setFormData({
      name: '',
      displayName: '',
      description: '',
      priceMonthly: 0,
      priceYearly: 0,
      isDefault: false,
      maxUsers: '',
      maxVehicles: '',
      features: '',
      moduleIds: [],
    });
    setShowModal(true);
  };

  const openEditModal = (pkg: SystemPackage) => {
    setSelectedSystemId(pkg.systemId);
    setEditingPackage(pkg);
    setFormData({
      name: pkg.name,
      displayName: pkg.displayName,
      description: pkg.description || '',
      priceMonthly: pkg.priceMonthly,
      priceYearly: pkg.priceYearly,
      isDefault: pkg.isDefault,
      maxUsers: pkg.maxUsers?.toString() || '',
      maxVehicles: pkg.maxVehicles?.toString() || '',
      features: pkg.features?.join('\n') || '',
      moduleIds: pkg.modules.filter(m => m.isIncluded).map(m => m.id),
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      const payload = {
        systemId: selectedSystemId,
        name: formData.name,
        displayName: formData.displayName,
        description: formData.description || undefined,
        priceMonthly: formData.priceMonthly,
        priceYearly: formData.priceYearly || formData.priceMonthly * 10,
        isDefault: formData.isDefault,
        maxUsers: formData.maxUsers ? parseInt(formData.maxUsers) : undefined,
        maxVehicles: formData.maxVehicles ? parseInt(formData.maxVehicles) : undefined,
        features: formData.features ? formData.features.split('\n').filter(f => f.trim()) : undefined,
        moduleIds: formData.moduleIds,
      };

      if (editingPackage) {
        await api.put(`/api/packages/${editingPackage.id}`, payload);
        // Uppdatera moduler separat
        await api.put(`/api/packages/${editingPackage.id}/modules`, { moduleIds: formData.moduleIds });
        toast.success('Paket uppdaterat');
      } else {
        await api.post('/api/packages', payload);
        toast.success('Paket skapat');
      }

      setShowModal(false);
      await loadData();
    } catch {
      toast.error(t('packages.saveError'));
    }
  };

  const toggleModule = (moduleId: string) => {
    setFormData(prev => ({
      ...prev,
      moduleIds: prev.moduleIds.includes(moduleId)
        ? prev.moduleIds.filter(id => id !== moduleId)
        : [...prev.moduleIds, moduleId],
    }));
  };

  // Hämta moduler för det valda systemet
  const getSystemModules = () => {
    return allModules.filter(m => m.systemId === selectedSystemId);
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-bgPrimary"></div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-bgContainer min-h-full">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-4 mb-2">
          <button onClick={() => navigate(ROUTES.SYSTEM_ADMINISTRATION)} className="p-2 hover:bg-bgElevated rounded-lg transition-colors">
            <ArrowLeftIcon className="w-5 h-5 text-textDefault" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-textDefault flex items-center gap-2">
              <CurrencyDollarIcon className="w-6 h-6 text-primary" />
              {t('packages.title')}
            </h1>
            <p className="text-textSecondary">{t('packages.description')}</p>
          </div>
        </div>
      </div>

      {/* Systems med paket */}
      <div className="space-y-4">
        {systems.map(system => {
          const isExpanded = expandedSystems.has(system.id);
          
          return (
            <div key={system.id} className="bg-bgElevated rounded-xl border border-border overflow-hidden">
              {/* System header */}
              <div className="flex items-center justify-between p-4 border-b border-border bg-bgBase">
                <button
                  onClick={() => toggleExpand(system.id)}
                  className="flex items-center gap-3 flex-1"
                >
                  {getIcon(system.icon)}
                  <span className="font-semibold text-textDefault">{system.displayName}</span>
                  <span className="text-sm text-textSecondary">
                    ({t('copy.admin.packageCount', { count: system.packages.length })})
                  </span>
                  {isExpanded ? (
                    <ChevronDownIcon className="w-4 h-4 text-textTertiary ml-2" />
                  ) : (
                    <ChevronRightIcon className="w-4 h-4 text-textTertiary ml-2" />
                  )}
                </button>
                <button
                  onClick={() => openCreateModal(system.id)}
                  className="px-3 py-1.5 bg-bgPrimary text-white rounded-lg text-sm flex items-center gap-1 hover:bg-bgPrimaryHover"
                >
                  <PlusIcon className="w-4 h-4" />
                  {t('packages.newPackage')}
                </button>
              </div>

              {/* Packages */}
              {isExpanded && (
                <div className="p-4 space-y-3">
                  {system.packages.length === 0 ? (
                    <div className="text-center py-8 text-textSecondary">
                      <SparklesIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>{t('copy.admin.packagesNoneYet')}</p>
                      <button
                        onClick={() => openCreateModal(system.id)}
                        className="mt-3 text-bgPrimary hover:underline"
                      >
                        {t('copy.admin.packagesCreateFirst')}
                      </button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {system.packages.map(pkg => (
                        <div
                          key={pkg.id}
                          className="p-4 border border-border rounded-lg bg-bgContainer hover:border-bgPrimary transition-colors"
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <h3 className="font-semibold text-textDefault flex items-center gap-2">
                                {pkg.displayName}
                                {pkg.isDefault && (
                                  <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 rounded-full">
                                    {t('packages.standard')}
                                  </span>
                                )}
                              </h3>
                              {pkg.description && (
                                <p className="text-sm text-textSecondary mt-1">{pkg.description}</p>
                              )}
                            </div>
                            <button
                              onClick={() => openEditModal(pkg)}
                              className="p-1 hover:bg-bgHover rounded"
                            >
                              <PencilIcon className="w-4 h-4 text-textSecondary" />
                            </button>
                          </div>
                          
                          <div className="mb-3">
                            <div className="text-2xl font-bold text-textDefault">
                              {formatPrice(pkg.priceMonthly)}
                            </div>
                            <div className="text-xs text-textTertiary">{t('packages.perMonth')}</div>
                          </div>

                          {/* Moduler */}
                          {pkg.modules.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                              {pkg.modules.filter(m => m.isIncluded).map(m => (
                                <span
                                  key={m.id}
                                  className="text-xs px-2 py-0.5 bg-bgBase text-textSecondary rounded"
                                >
                                  {m.displayName}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {systems.length === 0 && (
          <div className="text-center py-12 bg-bgElevated rounded-xl border border-border">
            <CubeIcon className="w-16 h-16 mx-auto text-textTertiary mb-4" />
            <p className="text-textSecondary">{t('copy.admin.packagesNoSystems')}</p>
          </div>
        )}
      </div>

      {/* Modal för att skapa/redigera paket */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-bgElevated rounded-xl border border-border w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-border">
              <h2 className="text-xl font-semibold text-textDefault">
                {editingPackage ? t('packages.editPackage') : t('packages.createNew')}
              </h2>
            </div>

            <div className="p-6 space-y-4">
              {/* Namn */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-textDefault mb-1">
                    {t('packages.internalName')}
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 bg-bgBase border border-border rounded-lg text-textDefault"
                    placeholder={t('packages.codeExample')}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-textDefault mb-1">
                    {t('packages.displayName')}
                  </label>
                  <input
                    type="text"
                    value={formData.displayName}
                    onChange={e => setFormData(prev => ({ ...prev, displayName: e.target.value }))}
                    className="w-full px-3 py-2 bg-bgBase border border-border rounded-lg text-textDefault"
                    placeholder={t('packages.nameExample')}
                  />
                </div>
              </div>

              {/* Beskrivning */}
              <div>
                <label className="block text-sm font-medium text-textDefault mb-1">
                  {t('common.description')}
                </label>
                <textarea
                  value={formData.description}
                  onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2 bg-bgBase border border-border rounded-lg text-textDefault"
                  rows={2}
                  placeholder={t('packages.descriptionPlaceholder')}
                />
              </div>

              {/* Priser */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-textDefault mb-1">
                    {t('copy.admin.pricePerMonthSek')}
                  </label>
                  <input
                    type="number"
                    value={formData.priceMonthly}
                    onChange={e => setFormData(prev => ({ ...prev, priceMonthly: parseFloat(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 bg-bgBase border border-border rounded-lg text-textDefault"
                    placeholder="500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-textDefault mb-1">
                    {t('copy.admin.pricePerYearSek')}
                  </label>
                  <input
                    type="number"
                    value={formData.priceYearly}
                    onChange={e => setFormData(prev => ({ ...prev, priceYearly: parseFloat(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 bg-bgBase border border-border rounded-lg text-textDefault"
                    placeholder="5000"
                  />
                  <p className="text-xs text-textTertiary mt-1">
                    {t('copy.admin.yearlyPriceAutoHint')}
                  </p>
                </div>
              </div>

              {/* Begränsningar */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-textDefault mb-1">
                    {t('packages.maxUsers')}
                  </label>
                  <input
                    type="number"
                    value={formData.maxUsers}
                    onChange={e => setFormData(prev => ({ ...prev, maxUsers: e.target.value }))}
                    className="w-full px-3 py-2 bg-bgBase border border-border rounded-lg text-textDefault"
                    placeholder={t('packagesManagement.unlimitedPlaceholder')}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-textDefault mb-1">
                    {t('packages.maxVehicles')}
                  </label>
                  <input
                    type="number"
                    value={formData.maxVehicles}
                    onChange={e => setFormData(prev => ({ ...prev, maxVehicles: e.target.value }))}
                    className="w-full px-3 py-2 bg-bgBase border border-border rounded-lg text-textDefault"
                    placeholder={t('packagesManagement.unlimitedPlaceholder')}
                  />
                </div>
              </div>

              {/* Default */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isDefault"
                  checked={formData.isDefault}
                  onChange={e => setFormData(prev => ({ ...prev, isDefault: e.target.checked }))}
                  className="w-4 h-4"
                />
                <label htmlFor="isDefault" className="text-sm text-textDefault">
                  {t('packages.defaultPackage')}
                </label>
              </div>

              {/* Moduler */}
              <div>
                <label className="block text-sm font-medium text-textDefault mb-2">
                  {t('packages.includedModules')}
                </label>
                <div className="grid grid-cols-2 gap-2 p-3 bg-bgBase border border-border rounded-lg max-h-48 overflow-y-auto">
                  {getSystemModules().map(module => (
                    <label
                      key={module.id}
                      className="flex items-center gap-2 p-2 hover:bg-bgHover rounded cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={formData.moduleIds.includes(module.id)}
                        onChange={() => toggleModule(module.id)}
                        className="w-4 h-4"
                      />
                      <span className="text-sm text-textDefault">{module.displayName}</span>
                    </label>
                  ))}
                  {getSystemModules().length === 0 && (
                    <p className="text-sm text-textSecondary col-span-2">
                      {t('packages.noModulesForSystem')}
                    </p>
                  )}
                </div>
              </div>

              {/* Features */}
              <div>
                <label className="block text-sm font-medium text-textDefault mb-1">
                  {t('packages.extraFeatures')}
                </label>
                <textarea
                  value={formData.features}
                  onChange={e => setFormData(prev => ({ ...prev, features: e.target.value }))}
                  className="w-full px-3 py-2 bg-bgBase border border-border rounded-lg text-textDefault"
                  rows={3}
                  placeholder={t('packagesManagement.phoneSupportPlaceholder')}
                />
              </div>
            </div>

            <div className="p-6 border-t border-border flex justify-end gap-3">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 border border-border rounded-lg text-textDefault hover:bg-bgHover"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 bg-bgPrimary text-white rounded-lg hover:bg-bgPrimaryHover"
              >
                {editingPackage ? t('common.saveChanges') : t('packages.createPackage')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

